import { useMemo } from 'react'
import type { TrackingPost, ChartConfig, ChartDataset } from '@/types'

export interface UseTrackingChartReturn {
  labels: string[]
  datasets: ChartDataset[]
}

const COLORS = {
  views: { border: '#667eea', bg: 'rgba(102, 126, 234, 0.1)' },
  likes: { border: '#e74c3c', bg: 'rgba(231, 76, 60, 0.1)' },
  comments: { border: '#9b59b6', bg: 'rgba(155, 89, 182, 0.1)' },
  shares: { border: '#f39c12', bg: 'rgba(243, 156, 18, 0.1)' },
  follows: { border: '#16a085', bg: 'rgba(22, 160, 133, 0.1)' },
}

export function useTrackingChart(
  trackingPosts: TrackingPost[],
  config: ChartConfig
): UseTrackingChartReturn {
  return useMemo(() => {
    const { mode, range, selectedPostId } = config

    const dateRange: Date[] = []
    const viewsData: number[] = []
    const likesData: number[] = []
    const commentsData: number[] = []
    const sharesData: number[] = []
    const followsData: number[] = []

    const makeRange = (startDate: Date, endDate: Date, maxDays = 365) => {
      const days: Date[] = []
      const start = new Date(startDate.getTime())
      const end = new Date(endDate.getTime())
      start.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)

      let current = start
      let cnt = 0
      while (current.getTime() <= end.getTime() && cnt < maxDays) {
        days.push(new Date(current.getTime()))
        current = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1)
        cnt++
      }
      return days
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // 범위 결정
    if (range === '7d') {
      for (let i = 6; i >= 0; i--) {
        dateRange.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i))
      }
    } else if (range === '30d') {
      for (let i = 29; i >= 0; i--) {
        dateRange.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i))
      }
    } else {
      // 'all'
      if (mode === 'individual' && selectedPostId) {
        const post = trackingPosts.find((p) => p.id === selectedPostId)
        if (post && post.metrics.length > 0) {
          const first = new Date(post.metrics[0].date)
          const last = new Date(post.metrics[post.metrics.length - 1].date)
          dateRange.push(...makeRange(first, last))
        } else {
          for (let i = 6; i >= 0; i--) {
            dateRange.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i))
          }
        }
      } else {
        let minDate: Date | null = null
        let maxDate: Date | null = null
        trackingPosts.forEach((post) => {
          post.metrics.forEach((m) => {
            const dt = new Date(m.date)
            dt.setHours(0, 0, 0, 0)
            if (!minDate || dt < minDate) minDate = new Date(dt)
            if (!maxDate || dt > maxDate) maxDate = new Date(dt)
          })
        })
        if (minDate && maxDate) {
          dateRange.push(...makeRange(minDate, maxDate))
        } else {
          for (let i = 6; i >= 0; i--) {
            dateRange.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i))
          }
        }
      }
    }

    // 데이터 계산
    if (mode === 'total') {
      dateRange.forEach((targetDate) => {
        let dayViews = 0,
          dayLikes = 0,
          dayComments = 0,
          dayShares = 0,
          dayFollows = 0

        trackingPosts.forEach((post) => {
          if (post.metrics.length === 0) return

          let latestMetricBeforeDate = null
          for (let i = post.metrics.length - 1; i >= 0; i--) {
            const metricDate = new Date(post.metrics[i].date)
            metricDate.setHours(0, 0, 0, 0)

            if (metricDate.getTime() <= targetDate.getTime()) {
              latestMetricBeforeDate = post.metrics[i]
              break
            }
          }

          if (latestMetricBeforeDate) {
            dayViews += Number(latestMetricBeforeDate.views) || 0
            dayLikes += Number(latestMetricBeforeDate.likes) || 0
            dayComments += Number(latestMetricBeforeDate.comments) || 0
            dayShares += Number(latestMetricBeforeDate.shares) || 0
            dayFollows += Number(latestMetricBeforeDate.follows || 0)
          }
        })

        viewsData.push(dayViews)
        likesData.push(dayLikes)
        commentsData.push(dayComments)
        sharesData.push(dayShares)
        followsData.push(dayFollows)
      })
    } else {
      // individual
      if (!selectedPostId) {
        dateRange.forEach(() => {
          viewsData.push(0)
          likesData.push(0)
          commentsData.push(0)
          sharesData.push(0)
          followsData.push(0)
        })
      } else {
        const selectedPost = trackingPosts.find((p) => p.id === selectedPostId)
        if (selectedPost && selectedPost.metrics.length > 0) {
          dateRange.forEach((targetDate) => {
            let dayViews = 0,
              dayLikes = 0,
              dayComments = 0,
              dayShares = 0,
              dayFollows = 0

            selectedPost.metrics.forEach((metric) => {
              const metricDate = new Date(metric.date)
              metricDate.setHours(0, 0, 0, 0)

              if (metricDate.getTime() === targetDate.getTime()) {
                dayViews += Number(metric.views) || 0
                dayLikes += Number(metric.likes) || 0
                dayComments += Number(metric.comments) || 0
                dayShares += Number(metric.shares) || 0
                dayFollows += Number(metric.follows || 0)
              }
            })

            viewsData.push(dayViews)
            likesData.push(dayLikes)
            commentsData.push(dayComments)
            sharesData.push(dayShares)
            followsData.push(dayFollows)
          })
        } else {
          dateRange.forEach(() => {
            viewsData.push(0)
            likesData.push(0)
            commentsData.push(0)
            sharesData.push(0)
            followsData.push(0)
          })
        }
      }
    }

    const labels = dateRange.map((d) =>
      d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
    )

    const datasets: ChartDataset[] = [
      {
        label: '조회수',
        data: viewsData,
        borderColor: COLORS.views.border,
        backgroundColor: COLORS.views.bg,
      },
      {
        label: '좋아요',
        data: likesData,
        borderColor: COLORS.likes.border,
        backgroundColor: COLORS.likes.bg,
      },
      {
        label: '댓글',
        data: commentsData,
        borderColor: COLORS.comments.border,
        backgroundColor: COLORS.comments.bg,
      },
      {
        label: '공유',
        data: sharesData,
        borderColor: COLORS.shares.border,
        backgroundColor: COLORS.shares.bg,
      },
      {
        label: '팔로우',
        data: followsData,
        borderColor: COLORS.follows.border,
        backgroundColor: COLORS.follows.bg,
      },
    ]

    return { labels, datasets }
  }, [trackingPosts, config])
}
